import React, { useMemo } from 'react';
import { FormRow, FormField, Box, Button, DatePicker, Text } from '../ui';
import type { ContractDetailsSchema } from '../voyageCharter/schema';
import type { VoyageCharterForm_CoaFragment } from '../../graphql';
import { exhaustiveCheck } from '../../lib/util';
import ReactSelect from 'react-select';
import { reactSelectStyles } from '../../lib/form-util';

type VoyageCharterFormContractDetails =
  | ContractDetailsSchema
  | { type: 'coa'; coaId: null; isPreliminary: boolean };

interface ContractTypeSelectProps {
  coas: VoyageCharterForm_CoaFragment[];
  contract: VoyageCharterFormContractDetails;
  defaultContract: ContractDetailsSchema | null;
  onChange: (contract: VoyageCharterFormContractDetails) => void;
  isMobileView: boolean;
}

export function BatchEditContractSelect({
  coas,
  contract,
  defaultContract,
  onChange,
  isMobileView,
}: ContractTypeSelectProps): JSX.Element {
  const coaOptions = useMemo(
    () =>
      coas.map((coa) => ({
        value: coa.id,
        label: `${coa.name} (Charterer: ${
          coa.organizationByChartererOrgId?.name
        }) - End Date: ${
          coa.endDate ? new Date(coa.endDate).toLocaleDateString() : 'N/A'
        }${coa.externalId ? ` - Ext ID: ${coa.externalId}` : ''}`,
      })),
    [coas],
  );

  const onContractTypeChange = (type: ContractDetailsSchema['type']) => {
    switch (type) {
      case 'coa': {
        onChange({
          type,
          coaId: defaultContract?.type === 'coa' ? defaultContract.coaId : null,
          isPreliminary:
            defaultContract?.type === 'coa'
              ? defaultContract.isPreliminary
              : false,
        });
        break;
      }
      case 'spot': {
        onChange({
          type,
          onSubsUntil:
            defaultContract?.type === 'spot'
              ? defaultContract.onSubsUntil
              : null,
        });
        break;
      }
      case 'inquiry': {
        onChange({
          type,
        });
        break;
      }
      default: {
        exhaustiveCheck(type);
      }
    }
  };

  const onOnSubsUntilChange = (newOnSubsUntil: Date | null) => {
    if (contract.type !== 'spot') return;

    onChange({
      ...contract,
      onSubsUntil: newOnSubsUntil?.toISOString() ?? null,
    });
  };

  const onCoaChange = (opt: { value: number; label: string } | null) => {
    if (opt) {
      onChange({
        type: 'coa',
        coaId: opt.value,
        isPreliminary: contract.type === 'coa' ? contract.isPreliminary : false,
      });
    }
  };

  const onIsPreliminaryChange = (newValue: boolean) => {
    if (contract.type !== 'coa') return;

    onChange({
      ...contract,
      isPreliminary: newValue,
    });
  };

  return (
    <FormRow
      style={{
        display: 'flex',
        flexDirection: isMobileView ? 'column' : 'row',
        marginBottom: '8px',
      }}
    >
      <FormField
        label="Contract type"
        width={220}
        {...(isMobileView && { marginBottom: '8px' })}
      >
        <Box display="flex" style={{ gap: '14px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input
              data-testid="radio-inquiry"
              type="radio"
              name="contractType"
              value="inquiry"
              onChange={(e) =>
                e.target.checked && onContractTypeChange('inquiry')
              }
              checked={contract.type === 'inquiry'}
            />{' '}
            Inquiry
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input
              data-testid="radio-coa"
              type="radio"
              name="contractType"
              value="coa"
              onChange={(e) => e.target.checked && onContractTypeChange('coa')}
              checked={contract.type === 'coa'}
            />{' '}
            COA
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input
              data-testid="radio-spot"
              type="radio"
              name="contractType"
              value="spot"
              onChange={(e) => e.target.checked && onContractTypeChange('spot')}
              checked={contract.type === 'spot'}
            />{' '}
            Spot
          </label>
        </Box>
      </FormField>
      {contract.type === 'coa' && (
        <Box
          display="flex"
          flexDirection={isMobileView ? 'column' : 'row'}
          alignItems="center"
          style={{ gap: '8px' }}
        >
          <FormField
            data-testid="select-coa-dropdown"
            label="Select COA"
            minWidth={220}
            flex={1}
          >
            <ReactSelect
              options={coaOptions}
              value={coaOptions.find((opt) => opt.value === contract.coaId)}
              onChange={onCoaChange}
              placeholder="Select COA"
              styles={reactSelectStyles}
              menuPosition="fixed"
            />
          </FormField>
          <FormField label="Options">
            <Box
              as="label"
              htmlFor="is_preliminary"
              display="inline-flex"
              alignItems="center"
            >
              <input
                id="is_preliminary"
                type="checkbox"
                checked={contract.type === 'coa' && contract.isPreliminary}
                onChange={(e) => onIsPreliminaryChange(e.currentTarget.checked)}
              />
              <Text isUnselectable ml={2}>
                Preliminary
              </Text>
            </Box>
          </FormField>
        </Box>
      )}
      {contract.type === 'spot' && (
        <Box display="flex">
          <FormField label="On subjects until" width={220}>
            <DatePicker
              showTimeInput
              onChange={onOnSubsUntilChange}
              selected={
                contract.type === 'spot' && contract.onSubsUntil
                  ? new Date(contract.onSubsUntil)
                  : null
              }
              timeZone="UTC"
              isClearable
            />
          </FormField>
          <FormField width={50}>
            <Button
              variant="secondary"
              onPress={() => onOnSubsUntilChange(null)}
            >
              Clear
            </Button>
          </FormField>
        </Box>
      )}
    </FormRow>
  );
}
